import type { KiroModelDeveloperBreakdownPoint } from '@ace-tracker/shared';
import { DeveloperName } from '../settings';
import { ExportMenu } from './ExportMenu';
import { InfoTooltip } from './InfoTooltip';

/**
 * Model x developer breakdown for Kiro — the counterpart to CodexDrillDownTable.
 * Token columns are estimates (see collector/docs/ARCHITECTURE.md "The Kiro
 * collector"), so their headers say so.
 */
export function KiroDrillDownTable({ breakdown, info }: { breakdown: KiroModelDeveloperBreakdownPoint[]; info?: string }) {
  const sorted = [...breakdown].sort((a, b) => (a.model === b.model ? b.events - a.events : a.model.localeCompare(b.model)));

  return (
    <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
      <ExportMenu title="Model x developer breakdown" className="float-right -mr-2 -mt-2 ml-2" />
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-text-default">
        Model x developer breakdown
        {info && <InfoTooltip text={info} />}
      </h3>
      <p className="mt-1 text-xs text-text-muted">Every (model, developer) pair with usage in this range. Token figures are estimates.</p>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
            <tr>
              <th className="pb-2.5 pr-3">Model</th>
              <th className="pb-2.5 pr-3">Developer</th>
              <th className="pb-2.5 pr-3 text-right">Turns</th>
              <th className="pb-2.5 pr-3 text-right">Est. input tokens</th>
              <th className="pb-2.5 pr-3 text-right">Est. output tokens</th>
              <th className="pb-2.5 text-right">Tool calls</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={`${row.model}:${row.developerId}`} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">{row.model}</td>
                <td className="py-2.5 pr-3">
                  <DeveloperName developerId={row.developerId} />
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.events}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.estimatedInputTokens.toLocaleString()}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.estimatedOutputTokens.toLocaleString()}</td>
                <td className="py-2.5 text-right tabular-nums">{row.toolCalls.toLocaleString()}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td className="py-3 text-text-muted" colSpan={6}>
                  No usage in this range.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
